/* vld-product.js - behaviour of sections/vld-product.liquid (VickersLDN wallet page with the vector editor).
 *
 * Reads its config from <script type="application/json" data-vld-config> inside [data-vld-product]:
 *   {sectionId, hub, designId, baseId, comboId, product (Shopify product JSON, prices in minor units), currency,
 *    productTitle, cdnBase, productId, swatches: [{colour, image, variantId}], gift: {price, compareAt, variants: [...]},
 *    giftFallback: {price, variants: [...]}, initialColour}
 * Needs vld-design.js + vld-editor.js (VLDEditor.mount) loaded before it.
 *
 * Cart contract (docs/kb/16): one wallet line, variant chosen by option NAME (Color / Inside Engraving / Back
 * Engraving), properties Front, Inside Engraving, Back Engraving, Gift Box, _design_id, _design_version, _combo_id,
 * _overrides (JSON), _preview_url; the gift box is its own line (the real gift-box variant).
 */
(function () {
  'use strict';
  const DESKTOP = '(min-width: 990px)';

  function init(root) {
    if (root.dataset.vldMounted) return;
    const cfgEl = root.querySelector('[data-vld-config]');
    if (!cfgEl) return;
    root.dataset.vldMounted = 'true';
    const cfg = JSON.parse(cfgEl.textContent);
    const product = cfg.product;
    const $ = (s, el) => (el || root).querySelector(s);
    const $$ = (s, el) => Array.prototype.slice.call((el || root).querySelectorAll(s));
    const money = c => (c / 100).toLocaleString('en-GB', { style: 'currency', currency: cfg.currency || 'GBP' });
    const norm = s => String(s || '').trim().toLowerCase().replace('hazlenut', 'hazelnut');
    // swatch discs cut from the real leather photos (hub /pub/swatches/<base>.json); null until loaded
    let hubSwatches = null;
    if (cfg.hub && window.fetch) {
      fetch(cfg.hub.replace(/\/$/, '') + '/pub/swatches/' + encodeURIComponent(cfg.baseId || '34') + '.json')
        .then(r => (r.ok ? r.json() : null))
        .then(map => {
          if (!map) return;
          hubSwatches = {};
          Object.keys(map).forEach(k => { if (map[k] && map[k].image) hubSwatches[norm(k)] = map[k].image; });
          if (typeof renderSwatches === 'function') renderSwatches();
        }).catch(() => {});
    }
    const mq = window.matchMedia(DESKTOP);

    // ---------- options by NAME (the Inside/Back-swapped bug in the old code came from fixed positions) ----------
    const optIndex = name => product.options.findIndex(o => norm(o.name || o) === norm(name));
    const iColour = optIndex('Color') >= 0 ? optIndex('Color') : optIndex('Colour');
    const iInside = optIndex('Inside Engraving');
    const iBack = optIndex('Back Engraving');
    const yes = v => /^(yes|true|1)$/i.test(String(v || '').trim());
    const surfaceOffered = view => {
      const index = view === 'inside' ? iInside : iBack;
      return index >= 0 && product.variants.some(v => yes(v.options[index]));
    };
    // any other option (Size on the keepsake box, Capacity...): buttons, matched by name (19 Oct 2026, KB 24)
    const extras = (product.options || []).map((o, i) => ({ name: o.name || o, i }))
      .filter(o => o.i !== iColour && o.i !== iInside && o.i !== iBack)
      .map(o => ({ ...o, values: [...new Set(product.variants.map(v => v.options[o.i]))] }));
    const extraSel = {};

    function findVariant(colour, inside, back) {
      return product.variants.find(v =>
        (iColour < 0 || norm(v.options[iColour]) === norm(colour)) &&
        (iInside < 0 || yes(v.options[iInside]) === !!inside) &&
        (iBack < 0 || yes(v.options[iBack]) === !!back) &&
        extras.every(o => v.options[o.i] === extraSel[o.name])) || null;
    }

    // ---------- gift box: the real product via all_products, else the known variant ids ----------
    const gift = (cfg.gift && cfg.gift.variants && cfg.gift.variants.length) ? cfg.gift : cfg.giftFallback;

    // ---------- state ----------
    const first = product.variants.find(v => v.available) || product.variants[0];
    const state = {
      colour: cfg.initialColour || (iColour >= 0 ? first.options[iColour] : ''),
      inside: false, back: false, gift: false,
      giftVariant: gift && gift.variants.length ? gift.variants[0] : null,
    };
    let editor = null, editorLive = false, fontsCat = null;
    const surfaces = {
      inside: {font: 'great-vibes', align: 'center', vertical: 'center', fill: 0.8},
      back: {font: 'great-vibes', align: 'center', vertical: 'center', fill: 0.8}
    };
    if(cfg.inlineStudio)Object.values(surfaces).forEach(s=>{s.position={x:.5,y:.5};s.fill=.65;});
    const surfaceSelection = () => JSON.stringify({
      colour: state.colour, inside: state.inside, back: state.back, surfaces,
      messages: ['inside', 'back'].map(v => ($('[data-addon="' + v + '"] input[type=text]') || {}).value || '')
    });
    function surfaceControls() {
      ['inside', 'back'].forEach(view => {
        const body = $('[data-addon="' + view + '"] .vld-addon__body');
        if (!body || body.querySelector('[data-surface-controls]')) return;
        const wrap = document.createElement('div');
        wrap.dataset.surfaceControls = view;
        wrap.className = 'vld-surface-controls';
        if(cfg.inlineStudio){
          const title=document.createElement('span');title.className='surface-control-label';title.textContent='Typeface';wrap.append(title);
          const fonts=document.createElement('div');fonts.className='surface-fonts';fonts.setAttribute('role','group');fonts.setAttribute('aria-label',view+' typeface');
          editor.messageFonts().forEach(f=>{
            const button=document.createElement('button');button.type='button';button.textContent=f.name;button.dataset.surfaceFont=f.id;
            button.style.fontFamily='vld-'+f.id;button.setAttribute('aria-pressed',String(f.id===surfaces[view].font));
            button.addEventListener('click',()=>{surfaces[view].font=f.id;fonts.querySelectorAll('button').forEach(b=>b.setAttribute('aria-pressed',String(b===button)));renderView(view);});
            fonts.append(button);
          });
          wrap.append(fonts);
          const size=document.createElement('label');size.textContent='Text size';
          const range=document.createElement('input');range.type='range';range.min='30';range.max='80';range.value=String(surfaces[view].fill*100);range.setAttribute('aria-label',view+' text size');range.dataset.surfaceSetting='fill';
          range.addEventListener('input',()=>{surfaces[view].fill=Number(range.value)/100;renderView(view);});size.append(range);wrap.append(size);
          const centre=document.createElement('button');centre.type='button';centre.className='surface-centre';centre.textContent='Centre message';
          centre.addEventListener('click',()=>{surfaces[view].position={x:.5,y:.5};renderView(view);});wrap.append(centre);
          const hint=document.createElement('p');hint.textContent='Drag your message on the preview to position it.';wrap.append(hint);
          const status=document.createElement('p');status.dataset.surfaceStatus=view;status.setAttribute('role','status');wrap.append(status);
          body.append(wrap);return;
        }
        const options = [
          ['font', 'Message font', editor.messageFonts().map(f => [f.id, f.name])],
          ['align', 'Alignment', [['left', 'Left'], ['center', 'Centre'], ['right', 'Right']]],
          ['vertical', 'Placement', [['top', 'Top'], ['center', 'Middle'], ['bottom', 'Bottom']]],
          ['fill', 'Text size', [['0.5', 'Small'], ['0.65', 'Medium'], ['0.8', 'Large']]]
        ];
        options.forEach(([key, title, choices]) => {
          const label = document.createElement('label'), select = document.createElement('select');
          label.textContent = title;
          select.dataset.surfaceSetting = key;
          select.setAttribute('aria-label', view + ' ' + title.toLowerCase());
          choices.forEach(([value, text]) => {
            const option = document.createElement('option');
            option.value = value; option.textContent = text; select.appendChild(option);
          });
          select.value = String(surfaces[view][key]);
          select.addEventListener('change', () => {
            surfaces[view][key] = key === 'fill' ? Number(select.value) : select.value;
            setView(view); renderView(view);
          });
          label.appendChild(select); wrap.appendChild(label);
        });
        const note = document.createElement('p');
        note.textContent = 'Placement stays within the engraving area. Longer messages automatically fit the available space.';
        wrap.appendChild(note); body.appendChild(wrap);
        const status = document.createElement('p');
        status.dataset.surfaceStatus = view;
        status.setAttribute('role', 'status');
        wrap.appendChild(status);
      });
    }
    extras.forEach(o => { extraSel[o.name] = first.options[o.i]; });
    function renderOptions() {
      $$('[data-vld-options]').forEach(box => {
        box.innerHTML = '';
        extras.forEach(o => {
          const g = document.createElement('div'); g.className = 'vld-opt';
          g.innerHTML = '<span class="lux-label">' + o.name + ': <span data-vld-opt-name></span></span><div class="vld-choices"></div>';
          g.querySelector('[data-vld-opt-name]').textContent = extraSel[o.name];
          o.values.forEach(val => {
            const b = document.createElement('button');
            b.type = 'button'; b.className = 'vld-choice' + (val === extraSel[o.name] ? ' on' : '');
            b.textContent = val;
            b.addEventListener('click', () => {
              extraSel[o.name] = val; renderOptions(); refresh();
              const variant = findVariant(state.colour, state.inside, state.back);
              document.dispatchEvent(new CustomEvent('luxury:variantChange', { detail: { variant: variant, colour: state.colour, options: variant ? variant.options : null } }));
            });
            g.querySelector('.vld-choices').appendChild(b);
          });
          box.appendChild(g);
        });
      });
    }

    // ---------- media: hero picture until the editor renders, then its canvas ----------
    const media = $('.luxury-media');
    const heroImg = $('.luxury-media__img');
    const heroBackdrop = $('.luxury-media__backdrop');
    const stageSlot = $('.vld-stage');
    const stageBg = $('.vld-stage__bg');
    const sheetPreview = $('.vld-sheet__preview');
    const sheetCanvas = $('.vld-sheet__canvas');
    const sheetBg = $('.vld-sheet__bg');
    const sheetImg = $('.vld-sheet__preview img');

    const heroUrls = colour => ({
      desktop: cfg.cdnBase + cfg.productId + '_' + norm(colour) + '_5376-3072.jpg',
      mobile: cfg.cdnBase + cfg.productId + '_' + norm(colour) + '_1080-1920.jpg',
    });

    function swapImage(urls) {
      if (!heroImg) return;
      const picture = heroImg.closest('picture');
      const sources = picture ? picture.querySelectorAll('source') : [];
      const newSrc = mq.matches ? urls.desktop : urls.mobile;
      const pre = new Image();
      const apply = () => {
        heroImg.classList.add('is-fading');
        setTimeout(() => {
          if (sources.length >= 2) { sources[0].srcset = urls.mobile; sources[1].srcset = urls.desktop; }
          heroImg.src = newSrc;
          if (heroBackdrop) heroBackdrop.src = urls.desktop;
          if (sheetImg) sheetImg.src = urls.mobile;
          heroImg.classList.remove('is-fading');
        }, 150);
      };
      pre.onload = apply; pre.onerror = apply;
      pre.src = newSrc;
    }

    function fadeUpdate(img, src) {
      if (!img) return;
      img.classList.add('is-fading');
      const t = new Image();
      t.onload = () => { img.src = src; img.classList.remove('is-fading'); };
      t.onerror = () => img.classList.remove('is-fading');
      t.src = src;
    }

    function updateCraftsmanship(colour) {
      if (cfg.isWallet === false) return;   // the inside / back tiles exist on the wallet only
      const c = norm(colour);
      fadeUpdate($('[data-vld-craft="inside-desktop"]'), cfg.cdnBase + c + '_inside_5376-3072.jpg');
      fadeUpdate($('[data-vld-craft="back-desktop"]'), cfg.cdnBase + c + '_back_5376-3072.jpg');
      fadeUpdate($('[data-vld-craft="inside-mobile"]'), cfg.cdnBase + c + '_inside_768-1376.jpg');
      fadeUpdate($('[data-vld-craft="back-mobile"]'), cfg.cdnBase + c + '_back_768-1376.jpg');
    }

    const srcSize = s => ({ w: s.naturalWidth || s.width, h: s.naturalHeight || s.height });
    function copyInto(c, src, blur) {
      // draw src (canvas or image) into c at src's size; blur = the soft backdrop: zoomed 12% and blurred inside its
      // own pixels (a CSS transform/filter would push the box out of the column and fade its edges)
      const { w, h } = srcSize(src);
      if (!w) return;
      if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
      const ctx = c.getContext('2d');
      if (blur) { ctx.filter = 'blur(60px)'; ctx.drawImage(src, -w * 0.06, -h * 0.06, w * 1.12, h * 1.12); ctx.filter = 'none'; }
      else ctx.drawImage(src, 0, 0, w, h);
    }
    function mirrorCanvas() {
      // the editor owns one canvas (the hero); the blurred backdrop, the mobile sheet preview and the Front thumbnail copy it
      const main = stageSlot && stageSlot.querySelector('.vld-ed__product');
      if (!main || !main.width) return;
      if (stageBg) copyInto(stageBg, main, true);
      if (sheetBg) copyInto(sheetBg, main, true);
      if (sheetCanvas) copyInto(sheetCanvas, main, false);
      paintThumb('front', main);
    }

    // ---------- swatches (same markup in the desktop column, the mobile bar and the sheet) ----------
    function renderSwatches() {
      $$('[data-vld-swatches]').forEach(box => {
        box.innerHTML = '';
        cfg.swatches.forEach(s => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'lux-swatch' + (norm(s.colour) === norm(state.colour) ? ' is-selected' : '');
          b.dataset.colour = s.colour;
          b.setAttribute('aria-label', 'Select ' + s.colour);
          b.title = s.colour;
          // disc = a crop of the real leather photo (hub /pub/swatches), else the variant image, else the name as a colour
          const hubSw = hubSwatches && hubSwatches[norm(s.colour)];
          if (hubSw) b.style.backgroundImage = 'url("' + hubSw + '")';
          else if (s.image) b.style.backgroundImage = 'url("' + s.image + '")';
          else b.style.backgroundColor = s.colour;
          b.addEventListener('click', () => selectColour(s.colour));
          box.appendChild(b);
        });
      });
      $$('[data-vld-colour-name]').forEach(e => { e.textContent = state.colour; });
    }

    function recipeColour(colour) {
      // the editor's colour keys come from recipes.json; match the Shopify option value case-insensitively
      if (!editor) return colour;
      const keys = editor.colours ? editor.colours() : [];
      return keys.find(k => norm(k) === norm(colour)) || colour;
    }

    function selectColour(colour) {
      state.colour = colour;
      $$('.lux-swatch').forEach(b => b.classList.toggle('is-selected', norm(b.dataset.colour) === norm(colour)));
      $$('[data-vld-colour-name]').forEach(e => { e.textContent = colour; });
      const urls = heroUrls(colour);
      if (editorLive) editor.setProduct({ colour: recipeColour(colour) });
      else swapImage(urls);
      updateCraftsmanship(colour);
      recolourViews();
      refresh();
      const variant = findVariant(colour, state.inside, state.back);
      document.dispatchEvent(new CustomEvent('luxury:variantChange', {
        detail: { variant: variant, colour: colour, options: variant ? variant.options : null },
      }));
    }

    // ---------- prices: everything from the variants; add-ons = difference vs the No/No variant ----------
    function priceOf(inside, back) {
      const v = findVariant(state.colour, inside, back);
      return v ? v.price : null;
    }

    function refresh() {
      const selected = findVariant(state.colour, state.inside, state.back);
      const unavailable = !selected || !selected.available;
      const v = selected || findVariant(state.colour, false, false) || first;
      let total = v.price, compare = v.compare_at_price || v.price;
      if (state.gift && state.giftVariant) { total += state.giftVariant.price; compare += state.giftVariant.compare_at_price || state.giftVariant.price; }
      // Shopify's compare-at is the same £40 on every variant, so once add-ons are in it can sit 1p above the price: only strike through a real saving
      const compareText = compare >= total * 1.05 ? money(compare) : '';
      $$('[data-vld-price]').forEach(e => { e.textContent = unavailable ? 'Unavailable' : money(total); });
      $$('[data-vld-compare]').forEach(e => { e.textContent = compareText; });
      const base = priceOf(false, false), pi = priceOf(true, false), pb = priceOf(false, true);
      const label = (p) => (p === null || base === null) ? '' : (p > base ? '+' + money(p - base) : 'Included');
      const ins = $('[data-addon="inside"] [data-addon-price]'), bk = $('[data-addon="back"] [data-addon-price]');
      if (ins) ins.textContent = label(pi);
      if (bk) bk.textContent = label(pb);
      ['inside', 'back'].forEach(view => {
        const toggle = $('[data-addon="' + view + '"] [data-toggle]');
        if (!toggle) return;
        const candidate = findVariant(state.colour, view === 'inside' ? true : state.inside, view === 'back' ? true : state.back);
        toggle.disabled = !state[view] && (!candidate || !candidate.available);
        const price = $('[data-addon="' + view + '"] [data-addon-price]');
        if (price && (!candidate || !candidate.available)) price.textContent = 'Unavailable for this selection';
      });
      const gp = $('[data-addon="gift"] [data-addon-price]');
      if (gp && state.giftVariant) gp.textContent = '+' + money(state.giftVariant.price);
      // the collapsed add-ons row (phones): what is on, else what is available
      const names = { inside: 'Inside engraving', back: 'Back engraving', gift: 'Gift box' };
      const offered = ['inside', 'back', 'gift'].filter(k => { const b = $('[data-addon="' + k + '"]'); return b && !b.hidden; });
      const on = offered.filter(k => state[k]).map(k => names[k]);
      $$('[data-addon-summary]').forEach(e => {
        e.textContent = on.length ? on.join(' · ') : offered.map(k => names[k].replace(' engraving', '')).join(' · ');
        e.classList.toggle('has-some', on.length > 0);
      });
      $$('[data-vld-atc]').forEach(b => { b.disabled = unavailable || (cfg.inlineStudio && !editor); b.querySelector('span').textContent = unavailable ? 'Choose an available option' : 'Add to cart · ' + money(total); });
      $$('[data-vld-open-sheet] span').forEach(s => { s.textContent = 'Personalise · ' + money(total); });
    }

    // ---------- add-ons ----------
    $$('.vld-addon').forEach(box => {
      const kind = box.dataset.addon, toggle = $('[data-toggle]', box), body = $('.vld-addon__body', box);
      const text = $('input[type=text]', box), count = $('[data-count]', box);
      if ((kind === 'inside' || kind === 'back') && !surfaceOffered(kind)) { box.hidden = true; return; }
      if (kind === 'gift' && !(gift && gift.variants.length)) { box.hidden = true; return; }
      toggle.addEventListener('change', () => {
        state[kind] = toggle.checked;
        if (body) body.hidden = !toggle.checked;
        if (toggle.checked && text) setTimeout(() => { text.focus({ preventScroll: true }); if (!mq.matches) text.scrollIntoView({ block: 'nearest' }); }, 50);   // the field sits right under the tick: no page jump on desktop
        refresh();
        if (kind === 'inside' || kind === 'back') { renderView(kind); if (toggle.checked) setView(kind); }
        if (kind === 'gift' && toggle.checked && giftImage()) setView('gift');
      });
      let typing = null;
      if (text && count) text.addEventListener('input', () => {
        count.textContent = text.value.length + '/' + text.maxLength;
        if (kind === 'inside' || kind === 'back') {
          if (views.current !== kind) setView(kind);
          clearTimeout(typing); typing = setTimeout(() => renderView(kind), 300);
        }
      });
      $$('[data-gift-variant]', box).forEach(b => b.addEventListener('click', () => {
        $$('[data-gift-variant]', box).forEach(x => x.classList.remove('on'));
        b.classList.add('on');
        state.giftVariant = gift.variants.find(g => String(g.id) === b.dataset.giftVariant) || state.giftVariant;
        refresh();
        if (giftImage()) { $$('[data-vld-views] [data-view="gift"] img').forEach(i => { i.src = giftThumbnail(); }); if (views.current === 'gift') paintHero('gift'); }
      }));
    });

    // ---------- the 4 views: Front (the live canvas), Inside / Back (the colour's photo, engraved live with the add-on
    // text: recipes.json bases[base].inside|back[colour] gives the plate + box; missing recipes/plates block engraving),
    // Gift box (the gift product's image) ----------
    const GALLERY = (cfg.gallery || []).filter(g => g && g.url);   // extra real photos ({pid}_gallery_*), e.g. inside the box
    const VIEWS = ['front', 'inside', 'back', 'gift'].filter(v => (v !== 'inside' || surfaceOffered(v)) && (v !== 'back' || surfaceOffered(v)) &&
      (v !== 'gift' || cfg.isWallet !== false)).concat(GALLERY.map((g, i) => 'g' + i));
    const galleryOf = v => /^g\d+$/.test(v) ? GALLERY[+v.slice(1)] : null;
    const VIEW_LABEL = { front: 'Front', inside: 'Inside', back: 'Back', gift: 'Gift box' };
    const views = { current: 'front', out: {}, key: {}, seq: {}, recipes: null, sig: null,
      alts: $$('[data-vld-alt]'), lists: $$('[data-vld-views]') };
    const viewPhoto = (view, colour) => cfg.cdnBase + norm(colour) + '_' + view + '_5376-3072.jpg';
    const giftImage = () => (state.giftVariant && state.giftVariant.image) || (gift && (gift.variants.find(v => v.image) || {}).image) || null;
    const giftThumbnail = () => (state.giftVariant && state.giftVariant.thumbnail) || giftImage();
    const thumbs = view => $$('[data-vld-views] [data-view="' + view + '"]');
    const photoCache = {};
    function loadPhoto(src) {
      // CORS first (so the pixels can be read and burnt); if the host refuses, a plain image (drawable, not readable)
      if (!photoCache[src]) photoCache[src] = VLDEditor.loadImage(src).catch(() => new Promise((res, rej) => {
        const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src;
      })).catch(e => { delete photoCache[src]; throw e; });
      return photoCache[src];
    }

    function buildViews() {
      views.lists.forEach(list => {
        list.innerHTML = '';
        list.hidden = VIEWS.filter(v => v !== 'gift' || giftImage()).length < 2;   // one view: no strip
        VIEWS.forEach(v => {
          if (v === 'gift' && !giftImage()) return;
          const gal = galleryOf(v);
          const label = gal ? String(gal.label).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c])) : VIEW_LABEL[v];
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'vld-view' + (v === views.current ? ' is-on' : ''); b.dataset.view = v;
          b.setAttribute('aria-pressed', v === views.current ? 'true' : 'false');
          b.setAttribute('aria-label', (gal ? gal.label : VIEW_LABEL[v]) + ' view');
          const img = gal ? gal.thumb : v === 'gift' ? giftThumbnail() : v === 'front' ? heroUrls(state.colour).desktop : viewPhoto(v, state.colour);
          b.innerHTML = '<span class="vld-view__thumb"><img src="' + img + '" alt="">' +
            (v === 'gift' || gal ? '' : '<canvas width="192" height="144" hidden></canvas>') + '<span class="vld-view__badge"></span></span>' +
            '<span class="vld-view__label">' + label + '</span>';
          b.addEventListener('click', () => setView(v));
          list.appendChild(b);
        });
      });
      ['inside', 'back'].filter(v => VIEWS.includes(v)).forEach(v => renderView(v));
    }

    function paintThumb(view, src) {
      // cover-crop src into the view's thumbnail canvases; null = back to the plain photo
      thumbs(view).forEach(b => {
        const c = b.querySelector('canvas');
        if (!c) return;
        if (!src) { c.hidden = true; return; }
        const { w, h } = srcSize(src);
        if (!w) return;
        const k = Math.max(c.width / w, c.height / h);
        const ctx = c.getContext('2d');
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.drawImage(src, (c.width - w * k) / 2, (c.height - h * k) / 2, w * k, h * k);
        c.hidden = false;
      });
    }

    function paintHero(view) {
      views.alts.forEach(alt => {
        const canvas = alt.querySelector('.vld-alt__canvas'), bg = alt.querySelector('.vld-alt__bg'), img = alt.querySelector('.vld-alt__img');
        const handle=alt.querySelector('.surface-drag-handle');if(handle)handle.hidden=true;
        if (view === 'gift') { img.src = giftImage() || ''; img.hidden = false; canvas.hidden = true; bg.hidden = true; return; }
        if (galleryOf(view)) { img.src = galleryOf(view).url; img.hidden = false; canvas.hidden = true; bg.hidden = true; return; }
        img.hidden = true; canvas.hidden = false; bg.hidden = false;
        const src = views.out[view];
        if (src) { copyInto(canvas, src, false); copyInto(bg, src, true); if(cfg.inlineStudio)surfaceHandle(alt,canvas,src,view); }
        else {
          canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
          bg.getContext('2d').clearRect(0, 0, bg.width, bg.height);
          img.src = viewPhoto(view, state.colour); img.hidden = false; canvas.hidden = true; bg.hidden = true;
        }
      });
    }

    function surfaceHandle(alt,canvas,source,view){
      const layout=source.vldTextLayout;if(!layout||!state[view])return;
      let handle=alt.querySelector('.surface-drag-handle');
      if(!handle){handle=document.createElement('button');handle.type='button';handle.className='surface-drag-handle';alt.append(handle);bindSurfaceDrag(handle,canvas);}
      handle.hidden=false;handle.dataset.surfaceView=view;
      handle.setAttribute('aria-label','Move '+view+' message. Drag or use arrow keys.');
      const r=canvas.getBoundingClientRect(),a=alt.getBoundingClientRect(),k=Math.min(r.width/canvas.width,r.height/canvas.height),b=layout.bounds;
      const x=r.left-a.left+(r.width-canvas.width*k)/2,y=r.top-a.top+(r.height-canvas.height*k)/2;
      Object.assign(handle.style,{left:(x+(b[0]+b[2])*k/2)+'px',top:(y+(b[1]+b[3])*k/2)+'px',width:Math.max(44,(b[2]-b[0])*k+12)+'px',height:Math.max(44,(b[3]-b[1])*k+12)+'px'});
    }
    function bindSurfaceDrag(handle,canvas){
      let drag=null,timer=null;
      const move=(x,y)=>{
        const origin=drag.layout.bbox;
        const px=origin[0]+x*(origin[2]-origin[0]),py=origin[1]+y*(origin[3]-origin[1]);
        const regions=drag.layout.regions||[];
        // Choose the closest usable region in plate coordinates. Never permit ink
        // in the gap between regions (for example a wallet's centre fold).
        const distance=r=>{const b=r.bbox;return Math.hypot(Math.max(b[0]-px,0,px-b[2]),Math.max(b[1]-py,0,py-b[3]));};
        const region=regions.reduce((best,r)=>!best||distance(r)<distance(best)?r:best,null);
        const b=region?region.bbox:origin;
        if(region)surfaces[drag.view].region=region.id;
        x=(px-b[0])/(b[2]-b[0]);y=(py-b[1])/(b[3]-b[1]);
        const safe=[b[0]+(b[2]-b[0])*.1,b[1]+(b[3]-b[1])*.1,b[2]-(b[2]-b[0])*.1,b[3]-(b[3]-b[1])*.1];
        const ink=drag.layout.bounds,w=Math.min(ink[2]-ink[0],safe[2]-safe[0]),h=Math.min(ink[3]-ink[1],safe[3]-safe[1]);
        const clamp=(v,lo,hi)=>Math.max(lo,Math.min(hi,v));
        surfaces[drag.view].position={x:clamp(x,(safe[0]+w/2-b[0])/(b[2]-b[0]),(safe[2]-w/2-b[0])/(b[2]-b[0])),y:clamp(y,(safe[1]+h/2-b[1])/(b[3]-b[1]),(safe[3]-h/2-b[1])/(b[3]-b[1]))};
        const view=drag.view,pos=surfaces[view].position,r=canvas.getBoundingClientRect(),a=handle.parentElement.getBoundingClientRect(),k=Math.min(r.width/canvas.width,r.height/canvas.height);
        handle.style.left=(r.left-a.left+(r.width-canvas.width*k)/2+(b[0]+pos.x*(b[2]-b[0]))*k)+'px';
        handle.style.top=(r.top-a.top+(r.height-canvas.height*k)/2+(b[1]+pos.y*(b[3]-b[1]))*k)+'px';
        if(!timer)timer=setTimeout(()=>{timer=null;renderView(view);},60);
      };
      handle.addEventListener('pointerdown',e=>{
        if(drag||e.isPrimary===false||(e.pointerType==='mouse'&&e.button!==0))return;
        const view=handle.dataset.surfaceView,source=views.out[view];if(!source?.vldTextLayout)return;
        drag={view,id:e.pointerId,start:canvasPoint(canvas,e),layout:source.vldTextLayout};handle.setPointerCapture(e.pointerId);handle.classList.add('is-dragging');e.preventDefault();
      });
      handle.addEventListener('pointermove',e=>{
        if(!drag||e.pointerId!==drag.id)return;
        const p=canvasPoint(canvas,e),b=drag.layout.bbox,pos=drag.layout.position;
        move(pos.x+(p.fx-drag.start.fx)*canvas.width/(b[2]-b[0]),pos.y+(p.fy-drag.start.fy)*canvas.height/(b[3]-b[1]));e.preventDefault();
      });
      const end=e=>{if(!drag||e.pointerId!==drag.id)return;const v=drag.view;drag=null;clearTimeout(timer);timer=null;handle.classList.remove('is-dragging');if(handle.hasPointerCapture(e.pointerId))handle.releasePointerCapture(e.pointerId);renderView(v);};
      handle.addEventListener('pointerup',end);handle.addEventListener('pointercancel',end);handle.addEventListener('lostpointercapture',end);
      handle.addEventListener('click',e=>e.preventDefault());handle.addEventListener('contextmenu',e=>e.preventDefault());
      handle.addEventListener('keydown',e=>{
        if(drag)return;
        const delta={ArrowLeft:[-.02,0],ArrowRight:[.02,0],ArrowUp:[0,-.02],ArrowDown:[0,.02]}[e.key];if(!delta)return;
        const view=handle.dataset.surfaceView,layout=views.out[view]?.vldTextLayout;if(!layout)return;e.preventDefault();
        drag={view,layout};move(layout.position.x+delta[0],layout.position.y+delta[1]);drag=null;
      });
      new ResizeObserver(()=>{const v=views.current,src=views.out[v];if(src)surfaceHandle(handle.parentElement,canvas,src,v);}).observe(canvas);
    }

    function setView(v) {
      if (VIEWS.indexOf(v) < 0 || (v === 'gift' && !giftImage())) return;
      views.current = v;
      $$('[data-vld-views] .vld-view').forEach(b => { const on = b.dataset.view === v; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      [media, sheetPreview].forEach(el => el && el.classList.toggle('is-alt', v !== 'front'));
      if (v !== 'front') paintHero(v);
      root.dispatchEvent(new CustomEvent('vld:view', {detail: {view: v}}));
    }

    async function viewRecipe(view, colour) {
      try {
        if (!views.recipes) views.recipes = editor && editor.recipes ? editor.recipes() : await fetch(cfg.hub.replace(/\/$/, '') + '/pub/recipes.json').then(r => r.json());
        const byView = views.recipes.bases[String(cfg.baseId || '34')][view];
        if (!byView) return null;
        const key = Object.keys(byView).find(k => norm(k) === norm(colour));
        if(!key)return null;
        const recipe=byView[key];
        const config=cfg.inlineStudio&&cfg.surfaceRegions?.bases?.[String(cfg.baseId)]?.[view]?.[key];
        if(!config?.regions?.length)return recipe;
        // Fail closed if a candidate describes a different plate or dimensions.
        if(config.plate!==recipe.plate||JSON.stringify(config.size)!==JSON.stringify(recipe.size))return null;
        const region=config.regions.find(r=>r.id===surfaces[view].region)||config.regions.find(r=>r.id===config.defaultRegion);
        if(!region)return null;
        surfaces[view].region=region.id;
        let choices=$('[data-region-choices="'+view+'"]');
        if(!choices){
          choices=document.createElement('div');choices.className='surface-region-choices';choices.dataset.regionChoices=view;
          choices.setAttribute('role','group');choices.setAttribute('aria-label','Message position');
          $('[data-surface-controls="'+view+'"]').prepend(choices);
          config.regions.forEach(r=>{const button=document.createElement('button');button.type='button';button.textContent=r.label;button.dataset.region=r.id;
            button.addEventListener('click',()=>{surfaces[view].region=r.id;renderView(view);});choices.append(button);});
        }
        choices.querySelectorAll('button').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.region===region.id)));
        return {...recipe,bbox:region.bbox,region:region.id,regions:config.regions,regionStatus:config.status};
      } catch (e) { return null; }
    }

    const surfaceRenders = {};
    function renderView(view) {
      // Wait for an in-flight render before reusing its cache key at checkout.
      const task = (surfaceRenders[view] || Promise.resolve()).catch(() => {}).then(() => renderSurface(view));
      surfaceRenders[view] = task;
      return task;
    }
    async function renderSurface(view) {
      if (!VIEWS.includes(view)) return;   // no inside / back on this product
      if (!window.VLDEditor || !VLDEditor.engraveText) return;
      const on = state[view], input = $('[data-addon="' + view + '"] input[type=text]');
      const text = on && input ? input.value.replace(/\s+/g, ' ').trim() : '';
      const settings = surfaces[view];
      const font = settings.font;
      const key = [state.colour, text, JSON.stringify(settings)].join('|');
      thumbs(view).forEach(b => b.classList.toggle('has-text', !!text));
      if (views.key[view] === key) return;
      const previous=views.out[view],samePlate=views.key[view]?.split('|')[0]===state.colour;
      views.key[view] = key;
      views.out[view] = null;
      paintThumb(view, null);
      if (views.current === view && !(cfg.inlineStudio&&previous&&samePlate)) paintHero(view);
      const n = views.seq[view] = (views.seq[view] || 0) + 1;
      if (views.current === view) views.alts.forEach(a => a.classList.add('is-busy'));
      let out = null;
      try {
        const photo = viewPhoto(view, state.colour);
        if (!text) out = await loadPhoto(photo);
        else {
          const rec = await viewRecipe(view, state.colour);
          let plate = null, recipe = rec;
          if (rec && rec.plate) {
            try { plate = await VLDEditor.loadImage(cfg.hub.replace(/\/$/, '') + '/pub/plate/' + rec.plate.split('/').map(encodeURIComponent).join('/')); } catch (e) { plate = null; }
          }
          if (!plate || !recipe || !recipe.bbox) throw new Error('Calibrated engraving preview unavailable');
          out = await VLDEditor.engraveText(plate, recipe, Object.assign({text: text}, settings));
          if(out.vldTextLayout&&recipe.regions)out.vldTextLayout.regions=recipe.regions;
        }
      } catch (e) { out = null; }
      if (n !== views.seq[view]) return;
      const status = $('[data-surface-status="' + view + '"]');
      if (status) status.textContent = text && !out ? 'Engraving preview unavailable. Please retry before adding this option.' : '';
      if (!out) delete views.key[view]; // allow retry after a transient image failure
      views.alts.forEach(a => a.classList.remove('is-busy'));
      views.out[view] = out;
      if(out?.vldTextLayout){surfaces[view].position=out.vldTextLayout.position;views.key[view]=[state.colour,text,JSON.stringify(surfaces[view])].join('|');}
      paintThumb(view, text && out ? out : null);
      if (views.current === view) paintHero(view);
    }

    function recolourViews() {
      thumbs('front').forEach(b => { b.querySelector('img').src = heroUrls(state.colour).desktop; });
      ['inside', 'back'].forEach(v => { thumbs(v).forEach(b => { b.querySelector('img').src = viewPhoto(v, state.colour); }); renderView(v); });
    }

    // a tap on the engraved photo (hero or sheet preview) goes to that word's field; on phones the sheet opens first
    // ... and a drag on it moves that word or piece of art (phones have no other way to move things)
    function canvasPoint(c, e) {
      const r = c.getBoundingClientRect(), fit = getComputedStyle(c).objectFit;
      const k = (fit === 'cover' ? Math.max : Math.min)(r.width / c.width, r.height / c.height);
      return {fx: (e.clientX - r.left - (r.width - c.width * k) / 2) / (c.width * k), fy: (e.clientY - r.top - (r.height - c.height * k) / 2) / (c.height * k)};
    }
    function heroTap(e) {
      const c = e.currentTarget;
      if (!editor || !editor.layerAt || !c.width) return;
      const {fx, fy} = canvasPoint(c, e);
      if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return;
      const id = editor.layerAt(fx, fy);
      if (!id) return;
      if (!mq.matches && sheet && !sheet.classList.contains('is-open')) { openSheet(); setTimeout(() => editor.focusLayer(id), 400); }
      else editor.focusLayer(id);
    }
    function heroDrag(c) {
      let drag = null, suppressClick = false, clickTimer = null;
      c.addEventListener('dragstart', e => e.preventDefault());
      c.addEventListener('contextmenu', e => e.preventDefault());
      c.addEventListener('pointerdown', e => {
        if (drag || e.isPrimary === false || (e.pointerType === 'mouse' && e.button !== 0)) return;
        if (!editor || !editor.layerAt || !editor.moveLayer || !c.width) return;
        const p = canvasPoint(c, e);
        if (p.fx < 0 || p.fx > 1 || p.fy < 0 || p.fy > 1) return;
        const id = editor.layerAt(p.fx, p.fy);
        if (!id) return;
        drag = {id: id, pointerId: e.pointerId, last: p, startX: e.clientX, startY: e.clientY, moved: false};
        e.preventDefault(); // own the layer gesture before native selection/long-press starts
        try { c.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
      });
      c.addEventListener('pointermove', e => {
        if (!drag || e.pointerId !== drag.pointerId) return;
        if (!drag.moved && Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < 6) return;   // a tap so far
        drag.moved = true;
        c.classList.add('is-dragging');
        const p = canvasPoint(c, e);
        editor.moveLayer(drag.id, p.fx - drag.last.fx, p.fy - drag.last.fy, false);
        drag.last = p;
        e.preventDefault();
      });
      const end = e => {
        if (!drag || e.pointerId !== drag.pointerId) return;
        const d = drag; drag = null;
        c.classList.remove('is-dragging');
        if (c.hasPointerCapture(e.pointerId)) c.releasePointerCapture(e.pointerId);
        if (d.moved) {
          editor.moveLayer(d.id, 0, 0, true);
          // Touch browsers may deliver the compatibility click after pointerup.
          // Never let a completed drag focus a text field and open the keyboard.
          suppressClick = true; clearTimeout(clickTimer);
          clickTimer = setTimeout(() => { suppressClick = false; }, 500);
        }
      };
      c.addEventListener('pointerup', end); c.addEventListener('pointercancel', end);
      c.addEventListener('lostpointercapture', end);
      c.addEventListener('click', e => { if (suppressClick) { suppressClick = false; clearTimeout(clickTimer); e.stopImmediatePropagation(); e.preventDefault(); } }, true);
      c.addEventListener('click', heroTap);
    }
    if (sheetCanvas) heroDrag(sheetCanvas);

    // ---------- the panel and the buy block live in the column on desktop, in the sheet on phones ----------
    const panel = $('.vld-panel'), buy = $('.vld-buy'), sheet = $('.vld-sheet'), addons = $('[data-vld-addons]'), meta = $('.vld-meta');
    let placedMobile = false;
    function placePanels() {
      const desk = cfg.inlineStudio || mq.matches;
      const panelSlot = desk ? $('[data-vld-slot="panel-desktop"]') : $('[data-vld-slot="panel-mobile"]');
      const buySlot = desk ? $('[data-vld-slot="buy-desktop"]') : $('[data-vld-slot="buy-mobile"]');
      if (panelSlot && panel.parentNode !== panelSlot) panelSlot.appendChild(panel);
      if (buySlot && buy.parentNode !== buySlot) buySlot.appendChild(buy);
      // add-ons: always open (no summary row) in the column; one collapsed row in the sheet until the customer opens it
      if (addons) { if (desk) addons.open = true; else if (!placedMobile) addons.open = false; }
      // the small print: under the button in the column, at the end of the scrolling fields in the sheet (its footer keeps total + button)
      if (meta) { const dest = desk ? buy : $('.vld-sheet__content'); if (dest && meta.parentNode !== dest) dest.appendChild(meta); }
      if (!desk) placedMobile = true;
      if (desk) closeSheet();
    }
    // the sheet is a modal dialog: focus starts on Back, Tab/Shift+Tab stay inside, Escape closes, focus returns to the opener
    const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let sheetOpener = null;
    const sheetFocusables = () => $$(FOCUSABLE, sheet).filter(el => el.offsetParent !== null || el === document.activeElement);
    function openSheet(e) {
      if (!sheet) return;
      sheetOpener = (e && e.currentTarget && e.currentTarget.focus) ? e.currentTarget : document.activeElement;
      sheet.classList.add('is-open'); sheet.setAttribute('aria-hidden', 'false'); sheet.setAttribute('aria-modal', 'true');
      document.body.classList.add('vld-sheet-open');
      mirrorCanvas();
      const back = $('[data-vld-close-sheet]', sheet) || sheetFocusables()[0];
      if (back) setTimeout(() => back.focus({ preventScroll: true }), 50);
    }
    function closeSheet() {
      if (!sheet) return;
      const wasOpen = sheet.classList.contains('is-open');
      sheet.classList.remove('is-open'); sheet.setAttribute('aria-hidden', 'true'); sheet.removeAttribute('aria-modal');
      document.body.classList.remove('vld-sheet-open');
      if (wasOpen && sheetOpener && sheetOpener.focus && document.contains(sheetOpener)) sheetOpener.focus({ preventScroll: true });
      sheetOpener = null;
    }
    $$('[data-vld-open-sheet]').forEach(b => b.addEventListener('click', openSheet));
    $$('[data-vld-close-sheet]').forEach(b => b.addEventListener('click', closeSheet));
    // "Personalise" in the value sections: scroll to the editor column on desktop, open the sheet on phones
    $$('[data-vld-personalise]').forEach(b => b.addEventListener('click', e => {
      e.preventDefault();
      if (mq.matches) {
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const first = panel && $('textarea, input[type=text]', panel);
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 450);
        return;
      }
      const opener = $$('[data-vld-open-sheet]').find(el => el.offsetParent !== null);
      if (opener) opener.click(); else openSheet(e);
    }));
    document.addEventListener('keydown', e => {
      if (!sheet || !sheet.classList.contains('is-open')) return;
      if (e.key === 'Escape') { closeSheet(); return; }
      if (e.key !== 'Tab') return;
      const f = sheetFocusables();
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0], last = f[f.length - 1], inside = sheet.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
    });
    // focus that still escapes (e.g. screen-reader navigation) is pulled back in
    document.addEventListener('focusin', e => {
      if (sheet && sheet.classList.contains('is-open') && !sheet.contains(e.target) && !mq.matches) {
        const f = sheetFocusables(); if (f.length) f[0].focus({ preventScroll: true });
      }
    });
    (mq.addEventListener ? mq.addEventListener('change', placePanels) : mq.addListener(placePanels));
    placePanels();

    // ---------- mobile bar: tighter once the hero has scrolled away ----------
    const bar = $('.lux-mobile-bar');
    if (bar) {
      const mediaEl = $('.lux-pdp__media');
      const update = () => bar.classList.toggle('is-scrolled', window.scrollY > (mediaEl ? mediaEl.offsetHeight * 0.125 : 125));
      window.addEventListener('scroll', update, { passive: true });
      update();
    }

    // ---------- lightbox for the craftsmanship tiles ----------
    const lightbox = $('.lux-craft-lightbox');
    if (lightbox) {
      const img = $('.lux-craft-lightbox__img', lightbox), lbl = $('.lux-craft-lightbox__label', lightbox);
      const close = () => { lightbox.classList.remove('is-active'); document.body.style.overflow = ''; };
      root.addEventListener('click', e => {
        const t = e.target.closest('.lux-craftsmanship__img');
        if (!t) return;
        img.src = t.src;
        lbl.textContent = (t.closest('.lux-craftsmanship__item') || {}).querySelector ? t.closest('.lux-craftsmanship__item').querySelector('.lux-craftsmanship__label').textContent : '';
        lightbox.classList.add('is-active'); document.body.style.overflow = 'hidden';
      });
      $('.lux-craft-lightbox__close', lightbox).addEventListener('click', e => { e.stopPropagation(); close(); });
      lightbox.addEventListener('click', e => { if (e.target === lightbox) close(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && lightbox.classList.contains('is-active')) close(); });
    }

    // ---------- the editor ----------
    async function mountEditor() {
      const slot = $('.vld-editor-slot');
      if (!slot || !cfg.designId || !window.VLDEditor) {
        if (slot) { slot.classList.add('is-failed'); slot.innerHTML = cfg.inlineStudio ? '' : '<p class="vld-editor-fallback">This design is engraved exactly as shown in the photo. Add a message inside or on the back below.</p>'; }
        root.dispatchEvent(new CustomEvent('vld:editor-status', {detail: {ready: false}}));
        return;
      }
      const edRoot = document.createElement('div');
      slot.appendChild(edRoot);
      try {
        editor = await window.VLDEditor.mount(edRoot, {
          hub: cfg.hub, designId: +cfg.designId, base: String(cfg.baseId || '34'),
          colour: state.colour, backdrop: cfg.backdrop || undefined,
          stableLayout: !!cfg.inlineStudio,
          customisation: cfg.customisation,
          dragAreas: cfg.surfaceRegions?.bases?.[String(cfg.baseId)]?.front,
          onColour: c => selectColour(c),
          onChange: s => {
            if (!editorLive) {
              editorLive = true;
              if (media) media.classList.add('is-live');
              if (sheetPreview) sheetPreview.classList.add('is-live');
            }
            mirrorCanvas();
            // the front changed (words, font, size, position, a new line): show the front again; a colour change does not count
            const sig = JSON.stringify(s.overrides.layers) + '|' + (s.added || []).length;
            if (!cfg.inlineStudio && views.sig !== null && sig !== views.sig && views.current !== 'front') setView('front');
            if (sig !== views.sig) { views.sig = sig; ['inside', 'back'].forEach(v => renderView(v)); }   // independent surface keys avoid changing their fonts
          },
        });
        views.recipes = editor.recipes ? editor.recipes() : views.recipes;
        // the engraved photo becomes the hero: move the stage into the media column
        const stage = edRoot.querySelector('.vld-ed__stage');
        if (stage && stageSlot) stageSlot.appendChild(stage);
        const mainCanvas = stage && stage.querySelector('.vld-ed__product');
        if (mainCanvas) heroDrag(mainCanvas);
        // Reset on the product photo itself (Matt 2 Nov 2026: "allow reset button to revert the design back to how it
        // was ... put that on the product screen"): the editor's own Reset, reachable without scrolling to the panel.
        if (stage && edRoot.querySelector('.vld-ed__reset') && !stage.querySelector('.vld-stage__reset')) {
          const rb = document.createElement('button');
          rb.type = 'button'; rb.className = 'vld-stage__reset'; rb.textContent = 'Reset design';
          rb.setAttribute('aria-label', 'Reset the design to how it started');
          rb.style.cssText = 'position:absolute;bottom:14px;left:14px;z-index:5;background:rgba(255,255,255,.92);color:#1a1a1a;border:1px solid rgba(0,0,0,.18);border-radius:999px;padding:7px 14px;font:inherit;font-size:13px;line-height:1;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.08)';
          rb.addEventListener('pointerdown', e => e.stopPropagation());
          rb.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); edRoot.querySelector('.vld-ed__reset').click(); });
          stage.appendChild(rb);
        }
        // design families (Matt 3 Nov 2026: one product per family per base): the family's recipient words as one-tap
        // picks above that field, and ?for=<word> on the link pre-fills one (ads and shelves keep their own word)
        const rp = (cfg.customisation && cfg.customisation.recipient_presets) || cfg.recipientPresets;
        if (rp && rp.layer && Array.isArray(rp.options) && rp.options.length > 1) {
          const sel = '.vld-ed__field[data-id="' + (window.CSS && CSS.escape ? CSS.escape(rp.layer) : rp.layer) + '"]';
          const area = () => edRoot.querySelector(sel + ' textarea');
          const mark = () => {
            const v = ((area() || {}).value || '').trim().toLowerCase();
            edRoot.querySelectorAll('.vld-picks__chip').forEach(b => { const on = b.textContent.toLowerCase() === v; b.setAttribute('aria-pressed', on); b.style.background = on ? '#1a1a1a' : '#fff'; b.style.color = on ? '#fff' : '#1a1a1a'; });
          };
          const setText = t => { const ta = area(); if (!ta) return false; ta.value = t; ta.dispatchEvent(new Event('input', { bubbles: true })); mark(); return true; };
          const place = () => {
            const f = edRoot.querySelector(sel);
            if (!f || f.querySelector('.vld-picks')) return;
            const box = document.createElement('div');
            box.className = 'vld-picks'; box.setAttribute('role', 'group'); box.setAttribute('aria-label', 'Quick picks');
            box.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;margin:6px 0 8px';
            rp.options.forEach(o => {
              const b = document.createElement('button');
              b.type = 'button'; b.className = 'vld-picks__chip'; b.textContent = o;
              b.style.cssText = 'font:inherit;font-size:13px;line-height:1;padding:7px 12px;border-radius:999px;border:1px solid rgba(0,0,0,.25);background:#fff;color:#1a1a1a;cursor:pointer';
              b.addEventListener('click', () => setText(o));
              box.appendChild(b);
            });
            const ta = f.querySelector('textarea');
            f.insertBefore(box, ta);
            ta.addEventListener('input', mark);
            mark();
          };
          place();
          const fieldsBox = edRoot.querySelector('.vld-ed__fields');
          if (fieldsBox) new MutationObserver(place).observe(fieldsBox, { childList: true });   // Reset rebuilds the fields
          const want = new URLSearchParams(location.search).get('for');
          if (want) {
            const word = rp.options.find(o => o.toLowerCase() === want.trim().toLowerCase()) || want.trim().slice(0, 21);
            if (word) setText(word);
          }
        }
        mirrorCanvas();   // the first draw happened before the stage was here: backdrop, sheet preview and Front thumbnail catch up
        new MutationObserver(() => stageSlot && stageSlot.classList.toggle('is-busy', edRoot.classList.contains('is-busy')))
          .observe(edRoot, { attributes: true, attributeFilter: ['class'] });
        // the recipe colour may differ in case from the option value: align now that the editor exists
        const rc = recipeColour(state.colour);
        if (rc !== state.colour) editor.setProduct({ colour: rc });
        try { fontsCat = await window.VLD.fonts(cfg.hub.replace(/\/$/, '') + '/pub/fonts.json'); } catch (e) { fontsCat = null; }
        surfaceControls();
        refresh();
        root.dispatchEvent(new CustomEvent('vld:editor-status', {detail: {ready: true}}));
      } catch (e) {
        slot.classList.add('is-failed');
        edRoot.innerHTML = cfg.inlineStudio ? '' : '<p class="vld-editor-fallback">The live preview could not load. Your ' + (cfg.noun || 'wallet') + ' will be engraved with the design in the photo.</p>';
        editor = null;
        refresh();
        root.dispatchEvent(new CustomEvent('vld:editor-status', {detail: {ready: false}}));
      }
    }

    // ---------- add to cart ----------
    function showError(msg) { $$('[data-vld-err]').forEach(e => { e.textContent = msg; e.hidden = !msg; }); }

    let addingToCart = false;
    async function addToCart() {
      if (addingToCart) return;
      addingToCart = true;
      try { await submitToCart(); }
      catch (error) { showError(error.message || 'Please try adding your gift again.'); }
      finally { addingToCart = false; }
    }
    async function submitToCart() {
      showError('');
      // Resolve normalised surface positions before snapshotting the order.
      for (const surface of ['inside', 'back']) {
        if (state[surface]) await renderView(surface);
      }
      if (cfg.inlineStudio && !editor) return showError('Please wait for your live preview to load before adding to cart.');
      const variant = findVariant(state.colour, state.inside, state.back);
      if (!variant) return showError('That combination is not available. Please choose another colour.');
      if (!variant.available) return showError('This option is sold out right now.');
      const props = {'_product_id': String(cfg.productId), '_base_id': String(cfg.baseId)};
      let artworkPreview = null;
      if (editor) {
        const s = editor.state();
        if (s.imagePending) return showError('Please wait while we prepare your image.');
        if (s.imageError) return showError(s.imageError);
        if (['Image','Both'].includes(cfg.customisation?.mode) && !s.imageUploads?.length) return showError('Choose your photo first, then review your personalised design.');
        props['_customisation_type'] = cfg.customisation?.mode || 'Text';
        if (s.imageUploads?.length) {
          if (Object.values(s.overrides.layers).some(layer=>layer.image && (!layer.image.asset?.job_id || !layer.image.asset?.sha256))) return showError('Please upload your image again so we can save it with your order.');
          props['Your image'] = s.imageUploads.map(x=>x.name).join(', ');
          props['_image_status'] = 'stored-artwork-for-production-review';
        }
        const texts = Object.values(s.texts || {}).map(t => String(t).replace(/\s+/g, ' ').trim()).filter(Boolean);
        if (texts.length) props['Front'] = texts.join(' / ').slice(0, 255);
        const fonts = [];
        Object.values(s.overrides.layers || {}).forEach(o => { if (o.font) fonts.push(fontsCat && fontsCat.fonts[o.font.split(':')[0]] ? fontsCat.fonts[o.font.split(':')[0]].name : o.font); });
        if (fonts.length) props['Font'] = Array.from(new Set(fonts)).join(', ');
        props['_design_id'] = String(s.design_id);
        props['_design_version'] = String(s.design_version || '');
        if (cfg.comboId) props['_combo_id'] = String(cfg.comboId);
        const renderOverrides = JSON.parse(JSON.stringify({ layers: s.overrides.layers || {}, added: s.added || [], ...(s.layout ? {layout: s.layout} : {}) }));
        for (const layer of Object.values(renderOverrides.layers)) {
          delete layer.imagePending; delete layer.imageError;
          if (layer.image) delete layer.image.src;
        }
        if (s.imageUploads?.length) {
          const beforePreview = JSON.stringify(editor.state());
          artworkPreview = await editor.previewBlob();
          if (JSON.stringify(editor.state()) !== beforePreview) return showError('Your design changed while saving. Please review it and try again.');
        }
        props['_overrides'] = JSON.stringify(renderOverrides);
        if (s.layout) props['_front_layout'] = JSON.stringify(s.layout);
        // the hosted preview must match the editor: same added lines, same design version (KB 20 P1)
        const q = new URLSearchParams({ o: JSON.stringify(renderOverrides), base: s.product.base,
          backdrop: s.product.backdrop, colour: s.product.colour, w: '800' });
        if (s.design_version) q.set('v', String(s.design_version));
        // Use the identical fixed layout, layer edits and version in the order preview.
        if (!artworkPreview) props['_preview_url'] = cfg.hub.replace(/\/$/, '') + '/pub/engraved/' + s.design_id + '.jpg?' + q;
      } else {
        props['_design_id'] = String(cfg.designId || '');
        if (cfg.comboId) props['_combo_id'] = String(cfg.comboId);
      }
      const insideText = ($('[data-addon="inside"] input[type=text]') || {}).value || '';
      const backText = ($('[data-addon="back"] input[type=text]') || {}).value || '';
      const submittedSelection = surfaceSelection();
      if (state.inside) { if (!insideText.trim()) return showError('Add your inside message, or untick inside engraving.'); props['Inside Engraving'] = insideText.trim(); }
      if (state.back) { if (!backText.trim()) return showError('Add your back engraving, or untick it.'); props['Back Engraving'] = backText.trim(); }
      const surfaceOrder = {};
      for (const view of ['inside', 'back']) {
        if (!state[view]) continue;
        await renderView(view);
        if (!views.out[view]) return showError('Please wait for the ' + view + ' engraving preview to load, then try again.');
        const recipe = await viewRecipe(view, state.colour);
        if (!recipe || !recipe.bbox || !recipe.plate) return showError('The ' + view + ' engraving preview is unavailable. Please try again before adding to your bag.');
        const settings = surfaces[view];
        const title = view === 'inside' ? 'Inside' : 'Back';
        props[title + ' font'] = editor && editor.fontName ? editor.fontName(settings.font) : settings.font;
        props[title + ' placement'] = recipe.regions?.find(r=>r.id===settings.region)?.label || (settings.position ? 'Custom position' : settings.align + ' / ' + settings.vertical);
        surfaceOrder[view] = Object.assign({}, settings, {
          text: (view === 'inside' ? insideText : backText).replace(/\s+/g, ' ').trim(),
          plate: recipe.plate, bbox: recipe.bbox, size: recipe.size, burn: recipe.burn,
          ...(recipe.regionStatus ? {regionStatus:recipe.regionStatus} : {})
        });
      }
      if (Object.keys(surfaceOrder).length) props['_engraving_surfaces'] = JSON.stringify({
        version: cfg.inlineStudio ? 2 : 1, base: String(cfg.baseId || ''), colour: state.colour, surfaces: surfaceOrder
      });
      if (submittedSelection !== surfaceSelection()) return showError('Your engraving choices changed. Please review them and add to your bag again.');
      const group = crypto.randomUUID();
      props['_personalisation_group'] = group;
      const items = [{ id: variant.id, quantity: 1, properties: props }];
      if (state.gift && state.giftVariant) {
        props['Gift Box'] = state.giftVariant.title;
        items.push({ id: state.giftVariant.id, quantity: 1, properties: { 'For': cfg.productTitle, '_personalisation_group': group } });
      }
      $$('[data-vld-atc]').forEach(b => { b.disabled = true; b.classList.add('is-shimmer'); });
      try {
        const body = { items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname };
        let payload = JSON.stringify(body), headers = {'Content-Type':'application/json','Accept':'application/json'};
        if (artworkPreview) {
          payload = new FormData(); headers = {'Accept':'application/json'};
          items.forEach((item,index)=>{
            payload.append(`items[${index}][id]`,item.id);
            payload.append(`items[${index}][quantity]`,item.quantity);
            Object.entries(item.properties).forEach(([key,value])=>payload.append(`items[${index}][properties][${key}]`,value));
          });
          payload.append('items[0][properties][_preview_url]',artworkPreview,'personalised-preview.jpg');
          payload.append('sections',body.sections);payload.append('sections_url',body.sections_url);
        }
        const r = await fetch('/cart/add.js', {method:'POST', headers, body:payload});
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        $$('[data-vld-voila]').forEach(e => e.classList.add('on'));
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { variantId: variant.id, items: items } }));
        await new Promise(resolve => setTimeout(resolve, 1000));
        {
          closeSheet();
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && data.sections && data.sections['cart-drawer']) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          } else if (!cfg.harness) {
            window.location.href = '/cart';
          }
          $$('[data-vld-atc]').forEach(b => { b.classList.remove('is-shimmer'); });
          refresh();
        } // Finished cart transition
      } catch (e) {
        showError(e.message || 'Could not add to cart. Please try again.');
        $$('[data-vld-atc]').forEach(b => { b.classList.remove('is-shimmer'); });
        refresh();
      }
    }
    $$('[data-vld-atc]').forEach(b => b.addEventListener('click', addToCart));

    // ---------- go ----------
    renderSwatches();
    renderOptions();
    updateCraftsmanship(state.colour);
    buildViews();
    refresh();
    document.dispatchEvent(new CustomEvent('luxury:variantChange', { detail: { variant: findVariant(state.colour, false, false), colour: state.colour } }));
    mountEditor();

    // test hook (harness only)
    root.__vld = { state: state, selectColour: selectColour, openSheet: openSheet, closeSheet: closeSheet, refresh: refresh,
      setView: setView, renderView: renderView, views: views,
      get editor() { return editor; }, get live() { return editorLive; } };
  }

  function boot() { document.querySelectorAll('[data-vld-product]').forEach(init); }
  window.VLDProduct = {mount: init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
